import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tournamentApi, eventApi, baiApi } from '../services/api';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';

const AFFILIATION_TYPES = ['None', 'Club', 'Association', 'Academy', 'Independent'];

const PublicRegistration = () => {
  const { id } = useParams();

  const [tournament, setTournament] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { player, event }
  const [error, setError] = useState(null);

  // Form state
  const [selectedEventId, setSelectedEventId] = useState('');
  const [form, setForm] = useState({
    player_name: '',
    player_email: '',
    player_phone: '',
    player2_name: '',
    player2_email: '',
    affiliation_type: 'None',
    affiliation_name: '',
    affiliation_id: '',
    notes: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});

  // BAI ID lookup (badminton only)
  const [baiId, setBaiId] = useState('');
  const [baiLookupLoading, setBaiLookupLoading] = useState(false);
  const [baiLookupResult, setBaiLookupResult] = useState(null); // player object or null
  const [baiLookupError, setBaiLookupError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [tRes, eRes] = await Promise.all([
          tournamentApi.getById(id),
          eventApi.list(id).catch(() => ({ data: [] })),
        ]);
        setTournament(tRes.data);
        const evs = (eRes.data || []).filter(e => e.status === 'registration' || e.status === 'open' || !e.status);
        setEvents(evs);
        if (evs.length === 1) setSelectedEventId(String(evs[0].id));
      } catch {
        setError('Tournament not found.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const selectedEvent = events.find(e => String(e.id) === selectedEventId);
  const isDoubles = selectedEvent && (
    selectedEvent.event_type === 'Doubles' || selectedEvent.event_type === 'Mixed Doubles'
  );

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) setFieldErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!selectedEventId) errs.event = 'Please select an event';
    if (!form.player_name.trim()) errs.player_name = 'Name is required';
    if (form.player_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.player_email.trim())) errs.player_email = 'Enter a valid email';
    if (isDoubles && !form.player2_name.trim()) errs.player2_name = "Partner's name is required";
    if (form.affiliation_type !== 'None' && !form.affiliation_name.trim()) errs.affiliation_name = 'Affiliation name is required';
    return errs;
  };

  const handleBaiLookup = async () => {
    if (!baiId.trim()) return;
    setBaiLookupLoading(true);
    setBaiLookupResult(null);
    setBaiLookupError(null);
    try {
      const res = await baiApi.lookup(baiId.trim());
      const player = res.data.player;
      setBaiLookupResult(player);
      // Always overwrite details from BAI lookup (re-verify should update fields)
      if (player.name) set('player_name', player.name);
      if (player.state) {
        set('affiliation_type', 'Association');
        set('affiliation_name', player.state);
      }
    } catch (err) {
      setBaiLookupError(err.response?.data?.error || 'Lookup failed. Check the ID and try again.');
    } finally {
      setBaiLookupLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setFieldErrors(errs); return; }

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        player_name: form.player_name.trim(),
        player_email: form.player_email.trim(),
        player_phone: form.player_phone.trim() || undefined,
        player2_name: isDoubles ? form.player2_name.trim() || undefined : undefined,
        player2_email: isDoubles ? form.player2_email.trim() || undefined : undefined,
        affiliation_name: form.affiliation_type !== 'None' ? form.affiliation_name.trim() || undefined : undefined,
        affiliation_id: form.affiliation_id.trim() || undefined,
        notes: form.notes.trim() || undefined,
        bai_id: baiLookupResult ? baiId.trim() || undefined : undefined,
      };
      const res = await eventApi.publicRegister(id, selectedEventId, payload);
      setSuccess({ player: res.data.player, event: selectedEvent, status: res.data.registrationStatus || 'registered' });
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading / Error ──────────────────────────────────────────────────────────

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner" />
    </div>
  );

  if (error && !tournament) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
      <p style={{ color: 'var(--text-secondary)' }}>{error}</p>
      <Link to="/tournaments" className="btn btn-primary">Browse Tournaments</Link>
    </div>
  );

  if (tournament && !['registration', 'draft'].includes(tournament.status)) return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>
      <MiniHero tournament={tournament} />
      <div style={{ maxWidth: '560px', margin: '2rem auto', padding: '0 1rem' }}>
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔒</div>
          <h3 style={{ margin: '0 0 0.5rem' }}>Registration Closed</h3>
          <p style={{ color: 'var(--text-secondary)', margin: '0 0 1.5rem' }}>
            Registration for this tournament is not currently open.
          </p>
          <Link to={`/t/${id}`} className="btn btn-primary">View Tournament</Link>
        </div>
      </div>
    </div>
  );

  if (tournament && tournament.online_registration_enabled === false) return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>
      <MiniHero tournament={tournament} />
      <div style={{ maxWidth: '560px', margin: '2rem auto', padding: '0 1rem' }}>
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📋</div>
          <h3 style={{ margin: '0 0 0.5rem' }}>Register via Contact</h3>
          <p style={{ color: 'var(--text-secondary)', margin: '0 0 1.5rem', fontSize: '0.9rem' }}>
            Online self-registration is not available for this tournament. Please contact the organizer to submit your entry.
          </p>
          {(tournament.registration_contact_name || tournament.registration_contact_phone || tournament.registration_contact_email) && (
            <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '1.25rem', marginBottom: '1.5rem', textAlign: 'left' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#6b7280', marginBottom: '0.75rem' }}>Registration Contact</div>
              {tournament.registration_contact_name && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  <span>👤</span><strong>{tournament.registration_contact_name}</strong>
                </div>
              )}
              {tournament.registration_contact_phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  <span>📞</span>
                  <a href={`tel:${tournament.registration_contact_phone}`} style={{ color: 'var(--primary-color)', fontWeight: '600', textDecoration: 'none' }}>
                    {tournament.registration_contact_phone}
                  </a>
                </div>
              )}
              {tournament.registration_contact_email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                  <span>✉️</span>
                  <a href={`mailto:${tournament.registration_contact_email}`} style={{ color: 'var(--primary-color)', fontWeight: '600', textDecoration: 'none' }}>
                    {tournament.registration_contact_email}
                  </a>
                </div>
              )}
            </div>
          )}
          <Link to={`/t/${id}`} className="btn btn-primary">View Tournament</Link>
        </div>
      </div>
    </div>
  );

  // ── Success screen ───────────────────────────────────────────────────────────

  if (success) return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <MiniHero tournament={tournament} />
      <div style={{ maxWidth: '560px', margin: '2rem auto', padding: '0 1rem' }}>
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>
            {success.status === 'pending' ? '⏳' : success.status === 'waitlisted' ? '📋' : '🎉'}
          </div>
          <h2 style={{ margin: '0 0 0.5rem', color: success.status === 'pending' ? '#92400e' : success.status === 'waitlisted' ? '#1e40af' : '#065f46' }}>
            {success.status === 'pending' ? 'Pending Approval' : success.status === 'waitlisted' ? 'Added to Waitlist' : "You're Registered!"}
          </h2>
          <p style={{ color: 'var(--text-secondary)', margin: '0 0 1.5rem', fontSize: '0.95rem' }}>
            {success.status === 'pending' && (
              <><strong>{success.player.display_name || success.player.player_name}</strong>'s registration for <strong>{success.event.name}</strong> is pending organizer approval. You'll be notified once confirmed.</>
            )}
            {success.status === 'waitlisted' && (
              <><strong>{success.player.display_name || success.player.player_name}</strong> has been added to the waitlist for <strong>{success.event.name}</strong>. You'll be notified if a spot opens up.</>
            )}
            {(!success.status || success.status === 'registered') && (
              <><strong>{success.player.display_name || success.player.player_name}</strong> has been registered for <strong>{success.event.name}</strong>.
                {success.player.player_email && <> A confirmation email will be sent to <strong>{success.player.player_email}</strong>.</>}
                {!success.player.player_email && <> Check back on the tournament page for draw and schedule updates.</>}
              </>
            )}
          </p>

          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '1rem 1.25rem', marginBottom: '1.5rem', textAlign: 'left' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem 1rem', fontSize: '0.85rem' }}>
              <span style={{ color: '#6b7280' }}>Event</span>
              <span style={{ fontWeight: '600' }}>{success.event.name}</span>
              {success.event.age_category && <>
                <span style={{ color: '#6b7280' }}>Category</span>
                <span style={{ fontWeight: '600' }}>{success.event.age_category}</span>
              </>}
              <span style={{ color: '#6b7280' }}>Type</span>
              <span style={{ fontWeight: '600' }}>{success.event.event_type}</span>
              {isDoubles && success.player.player2_name && <>
                <span style={{ color: '#6b7280' }}>Partner</span>
                <span style={{ fontWeight: '600' }}>{success.player.player2_name}</span>
              </>}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              className="btn btn-outline"
              onClick={() => { setSuccess(null); setForm({ player_name: '', player_email: '', player_phone: '', player2_name: '', player2_email: '', affiliation_type: 'None', affiliation_name: '', affiliation_id: '', notes: '' }); setSelectedEventId(''); setBaiId(''); setBaiLookupResult(null); setBaiLookupError(null); }}
            >
              Register Another
            </button>
            <Link to={`/t/${id}`} className="btn btn-primary">View Tournament</Link>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Registration form ────────────────────────────────────────────────────────

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>
      <MiniHero tournament={tournament} />

      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '1.5rem 1rem 3rem' }}>

        {/* Back link */}
        <Link to={`/t/${id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', color: 'var(--primary-color)', fontSize: '0.82rem', marginBottom: '1rem', textDecoration: 'none', fontWeight: '600' }}>
          ← Back to Tournament
        </Link>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', color: '#dc2626', fontSize: '0.85rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span>⚠</span> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>

          {/* ── Event selection ─────────────────────────── */}
          <FormCard>
            <SectionLabel>Select Event</SectionLabel>
            <Field required error={fieldErrors.event}>
              <select
                className="form-control"
                value={selectedEventId}
                onChange={e => setSelectedEventId(e.target.value)}
                style={{ fontWeight: selectedEventId ? '600' : 'normal' }}
              >
                <option value="">— Choose an event —</option>
                {events.map(ev => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name}
                    {ev.age_category ? ` (${ev.age_category})` : ''}
                    {ev.entry_fee > 0 ? ` — ₹${ev.entry_fee}` : ''}
                  </option>
                ))}
              </select>
            </Field>

            {selectedEvent && (
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
                <Tag color="teal">{selectedEvent.event_type}</Tag>
                {selectedEvent.age_category && <Tag color="amber">{selectedEvent.age_category}</Tag>}
                {selectedEvent.max_participants && (
                  <Tag color="gray">Max {selectedEvent.max_participants}</Tag>
                )}
                {selectedEvent.entry_fee > 0 && (
                  <Tag color="green">₹{selectedEvent.entry_fee} entry fee</Tag>
                )}
              </div>
            )}
          </FormCard>

          {/* ── BAI ID lookup (badminton only) ──────────── */}
          {tournament?.sport_type === 'badminton' && (
            <FormCard>
              <SectionLabel optional>BAI Player ID</SectionLabel>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <input
                  className="form-control"
                  type="text"
                  placeholder="e.g. 12345678 — auto-fills your details"
                  value={baiId}
                  onChange={e => { setBaiId(e.target.value); setBaiLookupResult(null); setBaiLookupError(null); }}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleBaiLookup())}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handleBaiLookup}
                  disabled={baiLookupLoading || !baiId.trim()}
                  style={{ whiteSpace: 'nowrap', minWidth: '80px', flexShrink: 0 }}
                >
                  {baiLookupLoading
                    ? <><span className="spinner" style={{ width: '12px', height: '12px', marginRight: '5px' }} />…</>
                    : 'Verify'}
                </button>
              </div>

              {baiLookupError && (
                <p style={{ margin: '0.4rem 0 0', fontSize: '0.78rem', color: '#dc2626' }}>{baiLookupError}</p>
              )}

              {baiLookupResult && (
                <div style={{ marginTop: '0.65rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '0.75rem 1rem' }}>
                  <div style={{ color: '#16a34a', fontWeight: '700', fontSize: '0.8rem', marginBottom: '0.5rem' }}>✓ Player verified</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.2rem 1rem', fontSize: '0.82rem' }}>
                    <span style={{ color: '#6b7280' }}>Name</span><span style={{ fontWeight: '600' }}>{baiLookupResult.name}</span>
                    {baiLookupResult.state && <><span style={{ color: '#6b7280' }}>State</span><span style={{ fontWeight: '600' }}>{baiLookupResult.state}</span></>}
                    {baiLookupResult.dob && <><span style={{ color: '#6b7280' }}>DOB</span><span style={{ fontWeight: '600' }}>{baiLookupResult.dob}</span></>}
                    {baiLookupResult.gender && <><span style={{ color: '#6b7280' }}>Gender</span><span style={{ fontWeight: '600' }}>{baiLookupResult.gender}</span></>}
                  </div>
                </div>
              )}
            </FormCard>
          )}

          {/* ── Player details ──────────────────────────── */}
          <FormCard>
            <SectionLabel>{isDoubles ? 'Player 1' : 'Your Details'}</SectionLabel>
            <Field label="Full Name" required error={fieldErrors.player_name}>
              <input className="form-control" type="text" placeholder="Your full name"
                value={form.player_name} onChange={e => set('player_name', e.target.value)} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.75rem' }}>
              <Field label="Email" error={fieldErrors.player_email}>
                <input className="form-control" type="email" placeholder="your@email.com"
                  value={form.player_email} onChange={e => set('player_email', e.target.value)} />
              </Field>
              <Field label="Phone" error={fieldErrors.player_phone}>
                <input className="form-control" type="tel" placeholder="+91 XXXXX XXXXX"
                  value={form.player_phone} onChange={e => set('player_phone', e.target.value)} />
              </Field>
            </div>
          </FormCard>

          {/* ── Partner details (doubles only) ──────────── */}
          {isDoubles && (
            <FormCard>
              <SectionLabel>Partner Details</SectionLabel>
              <Field label="Partner Name" required error={fieldErrors.player2_name}>
                <input className="form-control" type="text" placeholder="Partner's full name"
                  value={form.player2_name} onChange={e => set('player2_name', e.target.value)} />
              </Field>
              <Field label="Partner Email" error={fieldErrors.player2_email}>
                <input className="form-control" type="email" placeholder="partner@email.com"
                  value={form.player2_email} onChange={e => set('player2_email', e.target.value)} />
              </Field>
            </FormCard>
          )}

          {/* ── Affiliation ─────────────────────────────── */}
          <FormCard>
            <SectionLabel optional>Affiliation</SectionLabel>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: form.affiliation_type !== 'None' ? '0.85rem' : 0 }}>
              {AFFILIATION_TYPES.map(type => (
                <button
                  key={type} type="button"
                  onClick={() => set('affiliation_type', type)}
                  style={{
                    padding: '0.3rem 0.85rem', borderRadius: 999, border: '1.5px solid',
                    fontSize: '0.8rem', cursor: 'pointer', fontWeight: '600', transition: 'all 0.15s',
                    borderColor: form.affiliation_type === type ? 'var(--primary-color)' : '#d1d5db',
                    background: form.affiliation_type === type ? 'var(--primary-color)' : 'white',
                    color: form.affiliation_type === type ? 'white' : '#6b7280',
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
            {form.affiliation_type !== 'None' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.75rem' }}>
                <Field label={`${form.affiliation_type} Name`} required error={fieldErrors.affiliation_name} style={{ gridColumn: '1 / -1' }}>
                  <input className="form-control" type="text" placeholder={`Enter ${form.affiliation_type.toLowerCase()} name`}
                    value={form.affiliation_name} onChange={e => set('affiliation_name', e.target.value)} />
                </Field>
                <Field label="Membership ID" error={fieldErrors.affiliation_id} style={{ gridColumn: '1 / -1' }}>
                  <input className="form-control" type="text" placeholder="Membership number (optional)"
                    value={form.affiliation_id} onChange={e => set('affiliation_id', e.target.value)} />
                </Field>
              </div>
            )}
          </FormCard>

          {/* ── Notes ───────────────────────────────────── */}
          <FormCard>
            <SectionLabel optional>Notes</SectionLabel>
            <textarea className="form-control" rows={2} placeholder="Any special requests or notes…"
              value={form.notes} onChange={e => set('notes', e.target.value)}
              style={{ resize: 'vertical' }} />
          </FormCard>

          {/* ── Payment notice ───────────────────────────── */}
          {selectedEvent?.entry_fee > 0 && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '0.7rem 1rem', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
              <div style={{ fontSize: '0.8rem', color: '#92400e' }}>
                <strong>Entry fee:</strong> ₹{selectedEvent.entry_fee} {isDoubles ? 'per pair' : 'per player'} · payment at venue
              </div>
              <span style={{ fontWeight: '800', fontSize: '1rem', color: '#d97706', whiteSpace: 'nowrap' }}>₹{selectedEvent.entry_fee}</span>
            </div>
          )}

          {/* ── Submit ──────────────────────────────────── */}
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
            <Link to={`/t/${id}`} className="btn btn-outline" style={{ flexShrink: 0 }}>Cancel</Link>
            <button type="submit" className="btn btn-primary" disabled={submitting}
              style={{ flex: 1, fontWeight: '700', fontSize: '0.95rem', padding: '0.65rem 1rem' }}>
              {submitting
                ? <><span className="spinner" style={{ width: '14px', height: '14px', marginRight: '6px' }} />Registering…</>
                : '✓ Register Now'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

// ── Small helpers ─────────────────────────────────────────────────────────────

const MiniHero = ({ tournament }) => {
  const sportLabel = tournament?.sport_type
    ? tournament.sport_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : '';
  return (
    <div style={{ background: 'linear-gradient(135deg, var(--primary-color) 0%, #0D9488 100%)', color: 'white', padding: '1.25rem 1rem' }}>
      <div style={{ maxWidth: '560px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: '0 0 0.15rem', fontSize: '0.72rem', opacity: 0.75, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {sportLabel} · Registration
          </p>
          <h1 style={{ margin: 0, fontSize: 'clamp(1.05rem, 3.5vw, 1.4rem)', fontWeight: '800', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {tournament?.name}
          </h1>
        </div>
        <div style={{ fontSize: '0.78rem', opacity: 0.85, display: 'flex', gap: '0.75rem', flexWrap: 'wrap', flexShrink: 0 }}>
          {tournament?.venue && <span>📍 {tournament.venue}</span>}
          {tournament?.start_date && (
            <span>📅 {fmtDate(tournament.start_date)}{tournament.end_date && tournament.end_date !== tournament.start_date ? ` – ${fmtDate(tournament.end_date)}` : ''}</span>
          )}
        </div>
      </div>
    </div>
  );
};

const FormCard = ({ children }) => (
  <div style={{ background: 'white', borderRadius: '10px', padding: '1.1rem 1.25rem', marginBottom: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', border: '1px solid #e5e7eb' }}>
    {children}
  </div>
);

const SectionLabel = ({ optional, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.75rem' }}>
    <span style={{ fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#6b7280' }}>{children}</span>
    {optional && <span style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: '400' }}>optional</span>}
  </div>
);

const Field = ({ label, required, error, children, style }) => (
  <div style={{ marginBottom: '0.75rem', ...style }}>
    {label && (
      <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>
        {label}{required && <span style={{ color: '#dc2626', marginLeft: '2px' }}>*</span>}
      </label>
    )}
    {children}
    {error && <p style={{ margin: '0.2rem 0 0', fontSize: '0.76rem', color: '#dc2626' }}>{error}</p>}
  </div>
);

const Tag = ({ color, children }) => {
  const colors = {
    indigo: { bg: '#CCFBF1', color: '#115E59' },
    amber:  { bg: '#fef3c7', color: '#92400e' },
    gray:   { bg: '#f3f4f6', color: '#374151' },
    green:  { bg: '#d1fae5', color: '#065f46' },
  };
  const c = colors[color] || colors.gray;
  return (
    <span style={{ fontSize: '0.72rem', padding: '2px 8px', background: c.bg, color: c.color, borderRadius: 999, fontWeight: '600' }}>
      {children}
    </span>
  );
};

export default PublicRegistration;
